import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Image, Modal, Switch, Tag, Tooltip } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../../../common/constants';
import {
  fileUpload,
  formatDate,
  hasPermission,
  openNotification,
} from '../../../../../common/utils';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import { CREATE_AGE_GROUP, UPDATE_AGE_GROUP } from '../graphql/Mutations';
import {
  AGE_GROUPS_ADMIN,
  GET_AGE_GROUP_IMAGE_SIGNED_PUT_URL,
} from '../graphql/Queries';
import AgeGroupModal from './AgeGroupModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialSort = {
  field: 'minAge',
  order: 'ASC',
};

const AgeGroupTable = ({
  isAgeGroupModalOpen,
  setIsAgeGroupModalOpen,
  ageGroupImages,
  setAgeGroupImages,
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [ageGroupList, setAgeGroupList] = useState([]);
  const [ageGroupSearchTerm, setAgeGroupSearchTerm] = useState('');
  const [isEmptyAgeGroupList, setIsEmptyAgeGroupList] = useState(false);
  const [ageGroup, setAgeGroup] = useState();
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [isAgeGroupLoading, setIsAgeGroupLoading] = useState(true);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [agePreviewImage, setAgePreviewImage] = useState();

  const [createAgeGroup] = useMutation(CREATE_AGE_GROUP, {
    onError: () => {},
  });

  const [updateAgeGroup] = useMutation(UPDATE_AGE_GROUP, {
    onError: () => {},
  });

  const [getAgeGroupImageSignedPutUrl] = useLazyQuery(
    GET_AGE_GROUP_IMAGE_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
      onError() {
        setIsBtnLoading(false);
      },
    },
  );

  const [ageGroupsAdmin] = useLazyQuery(AGE_GROUPS_ADMIN, {
    onCompleted: (response) => {
      setAgeGroupList(response?.ageGroupsAdmin?.data);
      if (
        response?.ageGroupsAdmin?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyAgeGroupList(true);
      } else {
        setIsEmptyAgeGroupList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.ageGroupsAdmin?.count,
      };
      setPaginationProp(pagination);
      setIsAgeGroupLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_AGE_GROUP,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsAgeGroupLoading(true);
    ageGroupsAdmin({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: ageGroupSearchTerm,
          isArchived: isArchiveFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsAgeGroupLoading(true);
    setSortedInfo(sorter);
    ageGroupsAdmin({
      variables: {
        filter: {
          ...initialFilter,
          skip,
          limit: pagination?.pageSize,
          search: ageGroupSearchTerm,
          isArchived: isArchiveFilter,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setAgeGroupSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsAgeGroupLoading(true);
    ageGroupsAdmin({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: isArchiveFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const handleArchive = async (id, isArchived) => {
    setIsArchiveLoading(true);
    const response = await updateAgeGroup({
      variables: {
        where: {
          id,
        },
        data: {
          isArchived: !isArchived,
        },
      },
    });
    if (response?.data) {
      setIsArchiveLoading(false);
      setIsAgeGroupLoading(true);
      setIsArchivePrompts(false);
      setAgeGroup();
      ageGroupsAdmin({
        variables: {
          filter: {
            ...initialFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: ageGroupSearchTerm,
            isArchived: isArchiveFilter,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSort,
        },
      });
    }
    setIsArchiveLoading(false);
  };

  const handleAgeGroup = async (values) => {
    setIsBtnLoading(true);
    const uuid = uuidv4();
    let response;
    let image = '';
    const minAgeInt = parseInt(values?.minAge, 10);
    const maxAgeInt = parseInt(values?.maxAge, 10);

    const facilitateIds = values?.facilitates?.map(
      (facilitate) => facilitate?.key || facilitate,
    );

    if (values?.image && !ageGroupImages?.[0]?.url) {
      const { name } = values?.image?.[0];
      const ext = name?.substring(name?.lastIndexOf('.') + 1);
      const timestamp = Date?.now();
      const filename = name?.split('.')?.slice(0, -1)?.join('.');
      const newFilename = `${timestamp}_${filename}.${ext}`;
      const fileKey = `ageGroup/${uuid}/${newFilename}`;

      const res = await getAgeGroupImageSignedPutUrl({
        variables: {
          data: {
            fileName: fileKey,
            minAge: minAgeInt,
            maxAge: maxAgeInt,
          },
        },
      });
      if (res?.data) {
        try {
          try {
            await fileUpload(
              res?.data?.getAgeGroupImageSignedPutUrl?.signedUrl,
              ageGroupImages?.[0]?.originFileObj,
            );
            image = res?.data?.getAgeGroupImageSignedPutUrl?.fileName;
          } catch (error) {
            throw new Error(
              `${ageGroupImages?.name} upload failed. Please try again.`,
            );
          }
        } catch (error) {
          setIsBtnLoading(false);
          openNotification('error', error.message);
        }
      }
    }
    try {
      if (!ageGroup) {
        response = await createAgeGroup({
          variables: {
            data: {
              minAge: minAgeInt,
              maxAge: maxAgeInt,
              image: ageGroupImages?.[0]?.url ? undefined : image,
              facilitateIds,
            },
          },
        });
      } else {
        response = await updateAgeGroup({
          variables: {
            where: {
              id: ageGroup?.id,
            },
            data: {
              minAge: minAgeInt,
              maxAge: maxAgeInt,
              image: ageGroupImages?.[0]?.url ? undefined : image,
              facilitateIds,
            },
          },
        });
      }
    } catch (error) {
      setIsBtnLoading(false);
      return error;
    }
    if (response?.data) {
      setIsAgeGroupModalOpen(false);
      setIsBtnLoading(true);
      setAgeGroup();
      form?.resetFields();
      ageGroupsAdmin({
        variables: {
          filter: {
            ...initialFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: ageGroupSearchTerm,
            isArchived: isArchiveFilter,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSort,
        },
      });
    }
    setIsBtnLoading(false);
  };

  const columns = [
    {
      title: 'AGE GROUP',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      render: (_, record) =>
        record?.minAge > 18 ? (
          <>
            {record?.minAge} - {record?.maxAge} Months (
            {Math.floor(record?.minAge / 12)} -{' '}
            {Math.floor(record?.maxAge / 12)} Years)
          </>
        ) : (
          <>
            {record?.minAge} - {record?.maxAge} Months
          </>
        ),
    },
    {
      title: 'IMAGE',
      dataIndex: 'image',
      key: 'image',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <span>
          {record?.image ? (
            <>
              <Image
                preview={{
                  visible: false,
                }}
                width={20}
                height={20}
                src={record?.image}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setAgePreviewImage(record?.image);
                }}
                className="pointer"
              />
            </>
          ) : (
            <span>No Images</span>
          )}
        </span>
      ),
    },
    {
      title: 'FACILITATES',
      dataIndex: 'facilitate',
      key: 'facilitate',
      width: 300,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        record?.facilitates?.length > 0
          ? record?.facilitates?.map((facilitate) => (
              <Tag key={facilitate?.id}>{facilitate?.name}</Tag>
            ))
          : '-',
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => (
        <>
          <div className="action-button">
            {isArchiveFilter ? (
              <Tooltip title="Unarchive" placement="top" zIndex={4}>
                <UnarchiveIcon
                  onClick={() => {
                    setIsArchivePrompts(true);
                    setAgeGroup(record);
                  }}
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Edit" placement="top" zIndex={4}>
                  <EditIcon
                    onClick={() => {
                      setIsAgeGroupModalOpen(true);
                      setAgeGroup(record);
                      setAgeGroupImages([{ url: record?.image }]);
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
                <Tooltip title="Archive" placement="top" zIndex={4}>
                  <ArchiveIcon
                    onClick={() => {
                      setIsArchivePrompts(true);
                      setAgeGroup(record);
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && !hasUpdatePermission) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyAgeGroupList ? '' : <span />,
  };

  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() => handleArchive(ageGroup?.id, ageGroup?.isArchived)}
        onCancel={() => {
          setIsArchivePrompts(false);
          setAgeGroup();
        }}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!ageGroup?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this age group?
      </Modal>
      <CommonPreview
        visible={visible}
        setVisible={setVisible}
        image={agePreviewImage}
        setImage={setAgePreviewImage}
      />
      {isAgeGroupModalOpen && (
        <AgeGroupModal
          form={form}
          onFinish={handleAgeGroup}
          loadings={isBtnLoading}
          isModalOpen={isAgeGroupModalOpen}
          setIsModalOpen={setIsAgeGroupModalOpen}
          ageGroup={ageGroup}
          setAgeGroup={setAgeGroup}
          ageGroupImages={ageGroupImages}
          setAgeGroupImages={setAgeGroupImages}
          setVisible={setVisible}
          setAgePreviewImage={setAgePreviewImage}
        />
      )}
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              onChange={(checked) => setIsArchiveFilter(checked)}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={ageGroupList || []}
        loading={isAgeGroupLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default AgeGroupTable;
